import api from './api'
import {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  VerificationRequest,
  User,
} from '@/types'

export interface LoginOTPPendingResponse {
  requires_otp: true
  otp_token: string
  expires_in: number
  message: string
  resends_remaining: number
}

// When OTP is disabled the backend returns tokens directly (same shape as AuthResponse)
export interface LoginDirectResponse {
  access_token: string
  refresh_token: string
  token_type: string
  user: User
}

export type LoginResponse = LoginOTPPendingResponse | LoginDirectResponse

export interface VerifyLoginOTPRequest {
  otp_token: string
  otp_code: string
}

export interface VerifyLoginOTPResponse {
  // success path — same as AuthResponse
  access_token?: string
  refresh_token?: string
  token_type?: string
  user?: User
  // failure path
  success?: boolean
  locked?: boolean
  locked_until?: string
  locked_minutes?: number
  retries_remaining?: number
  message?: string
}

export interface ResendLoginOTPResponse {
  success: boolean
  message: string
  resends_remaining: number
}

export const authService = {
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', data)
    return response.data
  },

  async verifyLoginOTP(data: VerifyLoginOTPRequest): Promise<VerifyLoginOTPResponse> {
    const response = await api.post<VerifyLoginOTPResponse>('/auth/verify-login-otp', data)
    return response.data
  },

  async resendLoginOTP(otp_token: string): Promise<ResendLoginOTPResponse> {
    const response = await api.post<ResendLoginOTPResponse>('/auth/resend-login-otp', { otp_token })
    return response.data
  },

  async register(data: RegisterRequest): Promise<{ message: string; email: string }> {
    const response = await api.post('/auth/register', data)
    return response.data
  },

  async verifyEmail(data: VerificationRequest): Promise<{ message: string }> {
    const response = await api.post('/auth/verify-email', {
      email: data.email,
      verification_code: data.code,
    })
    return response.data
  },

  async verifyMobile(data: VerificationRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/verify-mobile', {
      email: data.email,
      verification_code: data.code,
    })
    return response.data
  },

  async resendVerification(
    email: string,
    verification_type: 'email' | 'mobile'
  ): Promise<{ message: string }> {
    const response = await api.post('/auth/resend-verification', {
      email,
      verification_type,
    })
    return response.data
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/auth/me')
    return response.data
  },

  async logout(): Promise<void> {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
  },

  async completeProfile(data: {
    new_password: string
    new_password_confirm: string
    new_mobile: string
  }): Promise<{ success: boolean; message: string; user: User }> {
    const response = await api.post('/auth/complete-profile', data)
    return response.data
  },

  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/request-password-reset', { email })
    return response.data
  },

  async resetPasswordWithToken(
    token: string,
    data: { new_password: string; new_password_confirm: string; new_mobile: string }
  ): Promise<{ success: boolean; message: string; access_token: string; refresh_token: string; user: User }> {
    const response = await api.post(`/auth/reset-password/${token}`, data)
    return response.data
  },

  async resendMigrationWelcome(
    email: string,
    recaptchaToken: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/auth/migration/resend', {
      email,
      recaptcha_token: recaptchaToken,
    })
    return response.data
  },
}
